// src/util/connectPaymentsClient.js
// Helper to initialize a SCaT (Separate Charges & Transfers) PaymentIntent.
// 1. Stores per-payee snapshot.
// 2. Creates platform PaymentIntent with transfer_group + snapshot assertion.
// 3. Returns { clientSecret, paymentIntentId }.
//
// payees shape: { provider:{acctId,amount}, liquor:{acctId,amount}, crown:{acctId,amount}, glassware?:{acctId,amount} }
// Amounts are integer minor units (cents).

export async function initializeConnectPayment({ transactionId, currency = 'usd', amount, payees, providerAccountId, listingId, providerId }) {
  if (!transactionId) throw new Error('transactionId required');
  if (typeof amount !== 'number' || amount <= 0) throw new Error('amount (cents) required');
  if (!payees || typeof payees !== 'object') throw new Error('payees object required');

  // 1) Store snapshot
  {
    const res = await fetch('/api/connect/set-pricing-snapshot', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
  body: JSON.stringify({ transactionId, currency, payees, providerAccountId, listingId, providerId }),
    });
    if (!res.ok) {
      let errJson = {};
      try { errJson = await res.json(); } catch (_) {}
      throw new Error(errJson?.error || `Failed to set pricing snapshot (${res.status})`);
    }
  }

  // 2) Create PaymentIntent
  const res = await fetch('/api/connect/create-payment-intent', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
  body: JSON.stringify({ transactionId, amount, currency, assertSnapshotTotal: true, providerAccountId, listingId, providerId }),
  });
  if (!res.ok) {
    let errJson = {};
    try { errJson = await res.json(); } catch (_) {}
    throw new Error(errJson?.error || `Failed to create PaymentIntent (${res.status})`);
  }
  const body = await res.json();
  return body; // { clientSecret, paymentIntentId }
}

// New canonical server-driven initializer: send raw inputs only; server computes allocations.
export async function createServerPaymentIntent({
  transactionId,
  pricing,
  selections,
  otherAddOns,
  lineItems,
  storeAccounts,
  flags,
  providerAccountId,
  glasswareConnectAccountId,
  // New: allow server to resolve dynamic provider account via listing/provider IDs
  listingId,
  providerId,
  currency = 'usd',
}) {
  if (!transactionId) throw new Error('transactionId required');
  if (!pricing || typeof pricing !== 'object') throw new Error('pricing required');

  // Added debug: log provider account pre-flight to help diagnose missing account issues
  try {
    const red = v => (typeof v === 'string' && v.startsWith('acct_')) ? (v.slice(0,7)+'…'+v.slice(-4)) : v;
    const isPlaceholder = !providerAccountId || /placeholder|static|test|demo/i.test(providerAccountId);
    console.log('[connectPaymentsClient][preflight]', {
      transactionId,
      providerAccountId: red(providerAccountId),
      isPlaceholder,
      listingId,
      providerId,
      hasSelections: Array.isArray(selections) ? selections.length : 0,
      lineItemsCount: Array.isArray(lineItems) ? lineItems.length : 0,
    });
    if (isPlaceholder) {
      console.warn('[connectPaymentsClient] providerAccountId missing/placeholder; relying on server lookup');
    }
  } catch (_) {}

  const res = await fetch('/api/connect/create-payment-intent', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      transactionId,
      pricing,
      selections,
      otherAddOns,
      lineItems,
      storeAccounts,
      flags,
      providerAccountId,
      glasswareConnectAccountId,
      listingId,
      providerId,
      currency,
    }),
  });
  if (!res.ok) {
    let errJson = {};
    try { errJson = await res.json(); } catch (_) {}
    if (errJson?.error && /Provider not onboarded/.test(errJson.error) && transactionId) {
      console.warn('[connectPaymentsClient] Provider account unresolved; try diagnostic endpoint for details');
      try { console.warn('[connectPaymentsClient] Diagnostic hint URL', `/api/connect/diagnose-provider?transactionId=${encodeURIComponent(transactionId)}`); } catch(_) {}
    }
    throw new Error(errJson?.error || `Failed to create PaymentIntent (${res.status})`);
  }
  return res.json(); // { clientSecret, paymentIntentId }
}
